import { InputContainer } from "@/components/ui/forms/inputContainer"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { type FieldValueChange } from "@/typings/formik"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import { type FieldInputProps } from "formik"

type ProgramBundleTypeInputProps<T, K> = {
  label: string
  name: string
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  error?: React.JSX.Element
  defaultType?: ProgramBundleType
  isSwitchEnabled?: boolean
  className?: string
} & React.ComponentProps<"div">

export function ProgramBundleTypeInput<T, K>({
  label,
  name,
  isRequiredInput,
  setFieldValue,
  getFieldProps,
  error,
  isSwitchEnabled = true,
  className,
  ...props
}: ProgramBundleTypeInputProps<T, K>): React.JSX.Element {
  const programType = getFieldProps(name as T).value as ProgramBundleType
  const isVirtual = programType === ProgramBundleType.VIRTUAL
  const isOnOff = programType === ProgramBundleType.ON_OFF

  const handleTypeChange = (type: ProgramBundleType) => {
    if (isSwitchEnabled) setFieldValue(name, type)
  }

  return (
    <InputContainer
      isRequiredInput={isRequiredInput}
      label={label}
      name={name}
      error={error}
      className={className}
      {...props}
    >
      <div className="flex items-center gap-2">
        <div
          className={`flex grow items-center justify-center gap-2 rounded-4xl px-4 py-3 ${isSwitchEnabled ? "cursor-pointer" : ""} ${isVirtual ? "bg-primary-500" : "bg-neutral-200"}`}
          onClick={() => handleTypeChange(ProgramBundleType.VIRTUAL)}
        >
          <InfinityIcon
            color="currentColor"
            className={cn("shrink-0", isVirtual ? "text-secondary-500" : "text-neutral-500")}
            width={24}
            height={24}
          />
          <Text fontStyle="LM" className={isVirtual ? "text-secondary-500" : "text-neutral-500"}>
            {word("programBundle.typeOfPrograms.infitePrograms")}
          </Text>
        </div>
        <Text fontStyle="LM" className="flex text-textDefault-heading capitalize">
          {word("global.or")}
        </Text>
        <div
          className={`flex grow items-center justify-center gap-2 rounded-4xl px-4 py-3 ${isSwitchEnabled ? "cursor-pointer" : ""} ${isOnOff ? "bg-primary-500" : "bg-neutral-200"}`}
          onClick={() => handleTypeChange(ProgramBundleType.ON_OFF)}
        >
          <OnOffIcon
            color="currentColor"
            className={cn("shrink-0", isOnOff ? "text-secondary-500" : "text-neutral-500")}
            width={24}
            height={24}
          />
          <Text fontStyle="LM" className={isOnOff ? "text-secondary-500" : "text-neutral-500"}>
            {word("programBundle.typeOfPrograms.onOffPrograms")}
          </Text>
        </div>
      </div>
    </InputContainer>
  )
}
